import { useState, useEffect, useRef } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { 
  Play, 
  Heart, 
  Shield, 
  Lightbulb, 
  Users, 
  Clock,
  ChevronDown,
  CheckCircle,
  ArrowRight
} from "lucide-react";
import { useNavigate } from "react-router-dom";

interface TimelineItem {
  year: string;
  title: string;
  description: string;
  milestone: string;
}

interface ValueItem {
  icon: React.ElementType;
  title: string;
  description: string;
  color: string;
}

export default function FounderNotes() {
  const navigate = useNavigate();
  const [activeVideo, setActiveVideo] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "200%"]);

  const timelineItems: TimelineItem[] = [
    {
      year: "2023",
      title: "The Problem Identified",
      description: "Christina realizes documentation is stealing time from patient care",
      milestone: "Foundation"
    },
    {
      year: "2024",
      title: "Team Assembly",
      description: "PTs, engineers, and designers unite around a common vision",
      milestone: "Team"
    },
    {
      year: "2024",
      title: "Notation Development",
      description: "AI-powered documentation platform takes shape",
      milestone: "Product"
    },
    {
      year: "2025",
      title: "Launch & Growth",
      description: "Transforming PT documentation across clinics nationwide",
      milestone: "Impact"
    }
  ];

  const values: ValueItem[] = [
    {
      icon: Users,
      title: "Clinician-Led",
      description: "PT-founded and PT-driven. Every decision starts with: 'Does this make a therapist's day better?'",
      color: "#619ed3"
    },
    {
      icon: Shield,
      title: "Integrity & Compliance",
      description: "Secure, ethical design meeting the highest healthcare compliance standards.",
      color: "#5c91a4"
    },
    {
      icon: Lightbulb,
      title: "Smart, but Simple",
      description: "Advanced technology that feels natural and easy to use. No jargon. No friction.",
      color: "#495B25"
    },
    {
      icon: Heart,
      title: "Respect for the Profession",
      description: "Honoring the complexity, empathy, and expertise behind physical therapy.",
      color: "#619ed3"
    },
    {
      icon: Clock,
      title: "Time is Sacred",
      description: "Designing for efficiency, not distraction. Less time charting, more time caring.",
      color: "#5c91a4"
    },
    {
      icon: CheckCircle,
      title: "Evidence-Based Innovation",
      description: "Building solutions grounded in clinical research and real-world PT practice.",
      color: "#495B25"
    }
  ];

  const openVideoModal = (videoSrc: string) => {
    setActiveVideo(videoSrc);
  };

  const closeVideoModal = () => {
    setActiveVideo(null);
  };

  return (
    <>
      {/* Video Modal */}
      {activeVideo && (
        <div 
          className="fixed inset-0 backdrop-blur-lg z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(59, 130, 246, 0.7)' }}
          onClick={closeVideoModal}
        >
          <div 
            className="relative rounded-lg overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            style={{ 
              backgroundColor: '#000000',
              width: '700px',
              height: '700px',
              maxWidth: '90vw',
              maxHeight: '90vh'
            }}
          >
            <button
              onClick={closeVideoModal}
              className="absolute top-4 right-4 z-10 text-white bg-black/50 hover:bg-black/70 rounded-full p-2 transition-colors duration-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <video
              className="w-full h-full object-contain"
              autoPlay
              loop
              playsInline
              controls
            >
              <source src={activeVideo} type="video/mp4" />
            </video>
          </div>
        </div>
      )}

      <div ref={containerRef} className="min-h-screen overflow-hidden relative">
        {/* Hero Section - Compact Banner */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#0f2027] via-[#203a43] to-[#2c5364]">
          {/* Animated Background */}
          <motion.div 
            className="absolute inset-0 opacity-30"
            style={{ y: backgroundY }}
          >
          </motion.div>

          {/* Floating Orbs */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 0.3 }}
              transition={{ duration: 2 }}
              className="absolute top-20 left-20 w-96 h-96 bg-[#619ed3]/20 rounded-full blur-3xl"
            />
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 0.2 }}
              transition={{ duration: 2, delay: 1 }}
              className="absolute bottom-20 right-20 w-80 h-80 bg-[#5c91a4]/20 rounded-full blur-3xl"
            />
          </div>

                    <div className="relative px-6 py-40 md:py-48 lg:py-56">
            <div className="mx-auto max-w-4xl text-center">
              {/* Main Title */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="text-center"
              >
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight font-productSans">
                  Built with{" "}
                  <span className="bg-white text-[#0f2027] px-4 py-2 rounded-lg inline-block mt-2">
                    Clinicians
                  </span>{" "}
                  in mind.
                </h1>
                <p className="text-lg md:text-xl text-slate-200 max-w-3xl mx-auto leading-relaxed font-productSans">
                  Led by those who've lived it.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* The Founding Story Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white relative overflow-hidden">
          <div className="max-w-6xl mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 font-productSans">
                The{" "}
                <span className="bg-gradient-to-r from-[#619ed3] to-[#5c91a4] bg-clip-text text-transparent">
                  Founding Story
                </span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Born from real PT experience, built for lasting impact
              </p>
            </motion.div>



            {/* Christina's Detailed Story */}
            <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="space-y-6"
              >
                <div className="bg-gradient-to-br from-[#619ed3]/5 to-[#5c91a4]/5 rounded-2xl p-8 border border-[#619ed3]/10">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 font-productSans">
                    I'm Christina
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    A physical therapist who, like so many of us, was tired of letting documentation 
                    steal time from patient care and life outside the clinic.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    That's why I started FOWND. We're a team led by PTs, building tools for PTs — 
                    starting with Notation, an AI-powered assistant that helps you document in real time, 
                    stay compliant, and actually get your time back.
                  </p>
                  <div className="mt-6 p-4 bg-[#619ed3]/10 rounded-lg border-l-4 border-[#619ed3]">
                    <p className="text-[#1e3d3b] font-medium italic">
                      "We're here to support the profession — and the people behind it."
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="aspect-square rounded-2xl overflow-hidden border-4 border-[#619ed3]/20 hover:border-[#619ed3]/50 transition-all duration-300">
                  <img
                    src="/assets/images/teams/christina.png"
                    alt="Christina - Co-Founder"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 bg-white rounded-xl p-4 shadow-xl border border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#619ed3] to-[#5c91a4] rounded-full flex items-center justify-center">
                      <Users className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Co-Founder</p>
                      <p className="text-sm text-gray-600">PT, DPT</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Partition Line */}
            <div className="flex justify-center mb-20">
              <div className="w-3/4 max-w-md h-px bg-gradient-to-r from-transparent via-[#495B25] to-transparent opacity-30"></div>
            </div>

            {/* Video Section Title */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-productSans">
                Meet the{" "}
                <span className="bg-gradient-to-r from-[#619ed3] to-[#495B25] bg-clip-text text-transparent">
                  Founders
                </span>
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed font-productSans">
                Hear directly from the PTs who built Fownd to transform clinical documentation
              </p>
            </motion.div>

            {/* Founder Videos Section */}
            <div className="grid md:grid-cols-2 gap-12 items-stretch mb-20">
              {/* Christina's Video Section */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="text-center group flex flex-col"
              >
                <div className="relative mb-6 flex-grow">
                  <div className="relative aspect-[9/16] max-w-sm mx-auto overflow-hidden rounded-2xl border-4 border-[#619ed3]/20 hover:border-[#619ed3]/50 transition-all duration-300 cursor-pointer group-hover:scale-105 shadow-xl"
                       onClick={() => openVideoModal("https://s3.us-east-1.amazonaws.com/fownd.care/FoundersNote/WebsiteFounderIntro.mp4")}>
                    {/* Video Preview */}
                    <video
                      className="w-full h-full object-cover"
                      muted
                      playsInline
                      preload="metadata"
                    >
                      <source src="https://s3.us-east-1.amazonaws.com/fownd.care/FoundersNote/WebsiteFounderIntro.mp4#t=0.5" type="video/mp4" />
                    </video>
                    
                    {/* Play Overlay */}
                    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center group-hover:bg-black/40 transition-all duration-300">
                      <div className="text-center text-white">
                        <div className="w-20 h-20 bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-white/40 transition-all duration-300">
                          <Play className="w-10 h-10 text-white" />
                        </div>
                        <p className="text-2xl font-bold drop-shadow-lg font-productSans text-white">Christina's Story</p>
                        <p className="text-lg opacity-90 drop-shadow-md font-medium mt-2 text-white">Co-Founder & President</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-[#619ed3]/5 to-[#5c91a4]/5 backdrop-blur-md rounded-xl p-6 border border-[#619ed3]/20 shadow-lg hover:shadow-xl transition-all duration-300 h-full flex flex-col justify-between">
                  <div>
                    <h3 className="text-2xl font-bold mb-3 font-productSans" style={{ color: '#619ed3' }}>
                      The PT Experience
                    </h3>
                    <p className="text-gray-700 text-base leading-relaxed italic">
                      "I was tired of letting documentation steal time from patient care and life outside the clinic."
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Rachel's Video Section */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="text-center group flex flex-col"
              >
                <div className="relative mb-6 flex-grow">
                  <div className="relative aspect-[9/16] max-w-sm mx-auto overflow-hidden rounded-2xl border-4 border-[#5c91a4]/20 hover:border-[#5c91a4]/50 transition-all duration-300 cursor-pointer group-hover:scale-105 shadow-xl"
                       onClick={() => openVideoModal("https://s3.us-east-1.amazonaws.com/fownd.care/FoundersNote/WebsiteRachelIntro.mp4")}>
                    {/* Video Preview */}
                    <video
                      className="w-full h-full object-cover"
                      muted
                      playsInline
                      preload="metadata"
                    >
                      <source src="https://s3.us-east-1.amazonaws.com/fownd.care/FoundersNote/WebsiteRachelIntro.mp4#t=0.5" type="video/mp4" />
                    </video>
                    
                    {/* Play Overlay */}
                    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center group-hover:bg-black/40 transition-all duration-300">
                      <div className="text-center text-white">
                        <div className="w-20 h-20 bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-white/40 transition-all duration-300">
                          <Play className="w-10 h-10 text-white" />
                        </div>
                        <p className="text-2xl font-bold drop-shadow-lg font-productSans text-white">Rachel's Vision</p>
                        <p className="text-lg opacity-90 drop-shadow-md font-medium mt-2 text-white">Chief Design Officer</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-[#5c91a4]/5 to-[#495B25]/5 backdrop-blur-md rounded-xl p-6 border border-[#5c91a4]/20 shadow-lg hover:shadow-xl transition-all duration-300 h-full flex flex-col justify-between">
                  <div>
                    <h3 className="text-2xl font-bold mb-3 font-productSans" style={{ color: '#619ed3' }}>
                      The Design Philosophy
                    </h3>
                    <p className="text-gray-700 text-base leading-relaxed italic">
                      "Sculpting this vision to reality with intentional, user-centered design."
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Mission, Vision, Values Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#1F3D3B]/5 to-[#495B25]/5 relative">
          <div className="max-w-6xl mx-auto">
            {/* Mission & Vision */}
            <div className="grid md:grid-cols-2 gap-12 mb-20">
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white/80 backdrop-blur-md rounded-2xl p-8 border border-white/50 shadow-xl"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-4 font-productSans flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#619ed3] to-[#5c91a4] rounded-full flex items-center justify-center">
                    <Heart className="w-5 h-5 text-white" />
                  </div>
                  Our Mission
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  At FOWND, we replace healthcare's administrative burden with clinician-built, 
                  HIPAA-safe automation — so clinical expertise stays at the center of every visit.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-white/80 backdrop-blur-md rounded-2xl p-8 border border-white/50 shadow-xl"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-4 font-productSans flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#5c91a4] to-[#495B25] rounded-full flex items-center justify-center">
                    <Lightbulb className="w-5 h-5 text-white" />
                  </div>
                  Our Vision
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  To reshape the future of clinical care by giving providers more time to treat, 
                  connect, and heal — starting with physical therapy, and building a world where 
                  documentation never gets in the way of patient care.
                </p>
              </motion.div>
            </div>

            {/* Values Grid */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-productSans">
                Our{" "}
                <span className="bg-gradient-to-r from-[#619ed3] to-[#5c91a4] bg-clip-text text-transparent">
                  Values
                </span>
              </h3>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                The principles that guide everything we build
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white/80 backdrop-blur-md rounded-xl p-6 border border-white/50 shadow-lg hover:shadow-xl transition-all duration-300 group hover:scale-105"
                >
                  <div className="flex items-start gap-4">
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300"
                      style={{ backgroundColor: `${value.color}20`, border: `2px solid ${value.color}30` }}
                    >
                      <value.icon className="w-6 h-6" style={{ color: value.color }} />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2 font-productSans group-hover:text-[#1e3d3b] transition-colors">
                        {value.title}
                      </h4>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {value.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Interactive Timeline Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#1F3D3B] via-[#1F3D3B] to-[#495B25] relative overflow-hidden">
          <div className="max-w-6xl mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h3 className="text-3xl md:text-4xl font-bold text-white mb-4 font-productSans">
                Our{" "}
                <span className="bg-gradient-to-r from-[#619ed3] to-[#5c91a4] bg-clip-text text-transparent">
                  Journey
                </span>
              </h3>
              <p className="text-xl text-white/80 max-w-2xl mx-auto">
                Key milestones in transforming clinical documentation
              </p>
            </motion.div>

            {/* Timeline */}
            <div className="relative pb-20">
              {/* Timeline Line with Infinity Effect */}
              <div className="absolute left-1/2 transform -translate-x-1/2 w-1 hidden md:block" style={{ height: 'calc(100% + 80px)' }}>
                {/* Main timeline line */}
                <div className="w-full bg-white/20 rounded-full" style={{ height: 'calc(100% - 80px)' }} />
                
                {/* Infinity continuation effect */}
                <div className="w-1 h-20 bg-gradient-to-b from-white/20 to-transparent"></div>
              </div>

              {/* Infinity Symbol and Text - positioned outside timeline container */}
              <div className="absolute left-1/2 transform -translate-x-1/2 flex flex-col items-center hidden md:flex" style={{ bottom: '-40px' }}>
                <div className="w-0 h-0 border-l-[6px] border-r-[6px] border-t-[12px] border-l-transparent border-r-transparent border-t-white/40 mb-2"></div>
                <div className="text-white/60 text-xs font-medium tracking-wider">∞</div>
                <div className="text-white/40 text-xs mt-1">The journey continues...</div>
              </div>

              <div className="space-y-12">
                {timelineItems.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.2 }}
                    className={`flex items-center gap-8 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    {/* Content */}
                    <div className="flex-1">
                      <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="px-3 py-1 bg-[#619ed3]/20 text-[#619ed3] rounded-full text-sm font-semibold border border-[#619ed3]/30">
                            {item.year}
                          </span>
                          <span className="px-3 py-1 bg-white/10 text-white/80 rounded-full text-xs border border-white/20">
                            {item.milestone}
                          </span>
                        </div>
                        <h4 className="text-xl font-bold text-white mb-2 font-productSans">
                          {item.title}
                        </h4>
                        <p className="text-white/80 leading-relaxed">
                          {item.description}
                        </p>
        </div>
      </div>

                    {/* Timeline Node */}
                    <div className="hidden md:flex w-4 h-4 bg-gradient-to-br from-[#619ed3] to-[#5c91a4] rounded-full border-4 border-white/20 flex-shrink-0 relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-[#619ed3] to-[#5c91a4] rounded-full animate-pulse opacity-50 scale-150" />
                    </div>

                    {/* Spacer for alternating layout */}
                    <div className="flex-1 hidden md:block" />
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <h3 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight font-productSans">
                Ready to Join Our{" "}
                <span className="bg-gradient-to-r from-[#619ed3] to-[#5c91a4] bg-clip-text text-transparent">
                  Mission?
                </span>
              </h3>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Experience the future of clinical documentation. Built by clinicians, for clinicians.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <button
                  onClick={() => window.open("https://notation.fownd.care/", "_blank")}
                  className="group relative px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-xl bg-gradient-to-r from-[#619ed3] to-[#5c91a4] text-white font-productSans"
                >
                  <span className="flex items-center gap-2">
                    Start with Notation
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                  </span>
                  <div className="absolute inset-0 bg-white/20 rounded-full blur opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </button>
                <button
                  onClick={() => navigate('/about')}
                  className="px-8 py-4 rounded-full font-semibold text-lg border-2 border-[#619ed3]/30 text-[#1e3d3b] hover:bg-[#619ed3]/10 hover:border-[#619ed3]/60 transition-all duration-300 font-productSans"
                >
                  Meet the Team
                </button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
}
